import { useEffect, useRef, useCallback, memo } from 'react'
import { isMobile } from 'react-device-detect'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { FreeMode } from 'swiper/modules'
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react'
import { MultiLevelCategory } from '@shopby/shop-sdk'

import 'swiper/css'
import 'swiper/css/free-mode'

type PropsType = {
  item: MultiLevelCategory[]
  categoryDepth2Label: string | undefined
  categoryDepth2No: number
  categoryDepth3No: number
  categoryDepth4No: number
  setCategoryDepth3No: React.Dispatch<React.SetStateAction<number>>
  setCategoryDepth4No: React.Dispatch<React.SetStateAction<number>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setHasSentGtmEvent: React.Dispatch<React.SetStateAction<boolean>>
}

const Category = ({
  item,
  categoryDepth2Label,
  categoryDepth2No,
  categoryDepth3No,
  categoryDepth4No,
  setCategoryDepth3No,
  setCategoryDepth4No,
  setPage,
  setHasSentGtmEvent,
}: PropsType) => {
  const swiperRef = useRef<SwiperRef | null>(null)
  const location = useLocation()

  useEffect(() => {
    // 초기화 시 슬라이드 위치 설정
    const targetIndex = item.findIndex((el) => Number(el?.categoryNo) === Number(categoryDepth3No))
    if (targetIndex !== -1) {
      setTimeout(() => {
        swiperRef.current?.swiper.slideTo(targetIndex + 1)
      }, 200)
    } else if (categoryDepth2No && categoryDepth3No && categoryDepth2No === categoryDepth3No) {
      setTimeout(() => {
        swiperRef.current?.swiper.slideTo(0)
      }, 200)
    }
  }, [categoryDepth3No, item])

  const linkClickHandler = useCallback(() => {
    setPage(1)
    setCategoryDepth3No(categoryDepth2No)
    setCategoryDepth4No(categoryDepth2No)
  }, [setPage, setCategoryDepth3No, setCategoryDepth4No, categoryDepth2No])

  return (
    <CategoryWrap
      $categoryDepth2No={categoryDepth2No}
      $categoryDepth4No={categoryDepth4No}
      className={isMobile ? 'category_header' : 'category_header pc'}
    >
      <div className='category'>
        {!location.pathname.includes('festivalcategory') && <h2>{categoryDepth2Label}</h2>}
        <div className='inner'>
          <SwiperWrap
            ref={swiperRef}
            slidesPerView={'auto'}
            spaceBetween={12}
            breakpoints={{
              768: {
                spaceBetween: 20,
              },
            }}
            modules={[FreeMode]}
            freeMode={false}
            centeredSlidesBounds={true}
            slideToClickedSlide={true}
          >
            <SwiperSlideItem>
              <div className={Number(categoryDepth2No) === Number(categoryDepth3No) ? 'on' : ''}>
                <Link
                  to={`/category/${categoryDepth2No}/${categoryDepth2No}/${categoryDepth2No}`}
                  onClick={linkClickHandler}
                >
                  전체
                </Link>
              </div>
            </SwiperSlideItem>
            {item?.map((el: any, index: number) => {
              return (
                <SwiperSlideItem key={el.categoryNo}>
                  <MemoizedSliceItem
                    swiperRef={swiperRef}
                    el={el}
                    setPage={setPage}
                    setHasSentGtmEvent={setHasSentGtmEvent}
                    index={index}
                    categoryDepth2No={categoryDepth2No}
                    categoryDepth3No={categoryDepth3No}
                    setCategoryDepth3No={setCategoryDepth3No}
                    setCategoryDepth4No={setCategoryDepth4No}
                  ></MemoizedSliceItem>
                </SwiperSlideItem>
              )
            })}
          </SwiperWrap>
        </div>
      </div>
    </CategoryWrap>
  )
}

const SliceItem = ({
  el,
  categoryDepth2No,
  categoryDepth3No,
  setCategoryDepth3No,
  setCategoryDepth4No,
  swiperRef,
  index,
  setPage,
  setHasSentGtmEvent,
}: any) => {
  const itemRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (Number(el?.categoryNo) === Number(categoryDepth3No)) {
      setTimeout(() => {
        swiperRef.current?.swiper.slideTo(index + 1)
      }, 200)
    }
  }, [el?.categoryNo, categoryDepth3No, swiperRef, index])

  return (
    <div className={Number(el?.categoryNo) === Number(categoryDepth3No) ? 'on' : ''}>
      <Link
        ref={itemRef}
        to={`/category/${categoryDepth2No}/${el.categoryNo}/${el.categoryNo}`}
        onClick={() => {
          setPage(1)
          setCategoryDepth3No(el.categoryNo)
          setCategoryDepth4No(el.categoryNo)
          setHasSentGtmEvent(false)
        }}
        key={el?.categoryNo}
      >
        {el.label}
      </Link>
    </div>
  )
}

interface CategoryWrapProps {
  $categoryDepth2No?: number
  $categoryDepth4No?: number
}

const borderBottomStyle = ({ $categoryDepth2No, $categoryDepth4No }: CategoryWrapProps) =>
  $categoryDepth2No === $categoryDepth4No && '1px var(--colGray3) solid'

export const CategoryWrap = styled.div<CategoryWrapProps>`
  /* 모바일: 3depth 카테고리 영역 */
  padding: 4px 0 0 0;
  border-bottom: ${borderBottomStyle};

  // 스크롤이 올렸을 때 (페이지가 내려갈 때) 카테고리 메뉴 fixed
  &.fixed {
    padding-bottom: 48px;
    & .swiper {
      position: fixed;
      top: 60px;
      left: 0;
      width: 100%;
      background-color: var(--colWhite);
      z-index: 10;
      padding: 0 20px;

      // 첫번째 카테고리 after
      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        border-bottom: ${borderBottomStyle};
        z-index: -1;
      }
    }
  }

  & div.category {
    position: relative;
    width: 100%;
    padding: 0;
    top: 1px;
    & h2 {
      font-size: 20px;
      font-weight: 600;
      line-height: 24px;
      padding: 12px 0 0 20px;
      position: relative;
      top: 0;
      transition: 0.2s;
    }
  }

  @media screen and (min-width: 768px) {
    /* 웹: 2Depth 카테고리 영역 */
    position: relative;
    width: 100%;
    padding: 0;
    border-bottom: ${borderBottomStyle};

    // 스크롤 다운 될 때 fixed
    &.fixed {
      padding-bottom: 50px;
      & .inner {
        position: fixed;
        top: 84px;
        left: 0;
        background-color: var(--colWhite);
        z-index: 3;
        width: 100%;
        border-bottom: ${borderBottomStyle};

        & .swiper {
          position: relative;
          top: 1px;
          left: auto;
          width: 1216px;
          margin: 0 auto;
          padding: 0;

          // 첫번째 카테고리 after
          &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 1px;
            border-bottom: ${borderBottomStyle};
            z-index: -1;
          }
        }
      }
    }

    & div.category {
      position: relative;
      max-width: 1280px;
      margin: 0 auto;
      padding: 0 32px;
      z-index: 4;
      & h2 {
        color: var(--colBlack);
        font-size: 36px;
        font-weight: 600;
        line-height: 40px;
        padding: 32px 0 16px 0;
      }
    }
  }
`

// Tab Swiper 2Depth 영역
export const SwiperWrap = styled(Swiper)`
  overflow: hidden;
  z-index: 1;
  padding: 0 20px;

  & div.swiper-wrapper {
    & div.swiper-slide {
      width: auto !important;
      position: relative;
      & a {
        display: block;
        padding: 16px 2px;
        font-size: 16px;
        font-weight: 500;
        line-height: 18px;
        color: #a3a3a3;
      }
    }
    & div.on {
      & ::after {
        content: '';
        display: block;
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 2px;
        background: var(--colBlack);
        left: 0;
      }
      a {
        color: var(--colBlack);
        font-weight: 700;
      }
    }
  }

  @media screen and (min-width: 768px) {
    padding: 0;
    & div.swiper-wrapper {
      & div.swiper-slide {
        position: relative;
        & div {
          & p {
            font-size: 16px;
            font-weight: 500;
            line-height: 18px;
            color: #a3a3a3;
            cursor: pointer;
          }
        }
        & div.on {
          & p {
            font-weight: 700;
            color: var(--colBlack);
          }
          & a::after {
            content: '';
            display: block;
            position: absolute;
            bottom: 0px;
            width: 100%;
            height: 2px;
            background: var(--colBlack);
            left: 0;
          }
        }
      }
    }
  }
`

export const SwiperSlideItem = styled(SwiperSlide)``

const MemoizedSliceItem = memo(SliceItem)
export default memo(Category)
